import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Drama, MapPin, Clock, Shirt, Sparkles, ShieldCheck, Minus, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getNyeTiers, createNyeCheckout } from "@/lib/api";

const EVENT_TS = new Date("2026-10-31T17:55:00-05:00").getTime();

const ink = "#17130c";
const parchment = "#f4ecdd";
const parchment2 = "#efe4cf";
const gold = "#b98f2f";
const goldHi = "#d9b45c";
const oxblood = "#6e1423";

function useEventCountdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, EVENT_TS - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    mins: Math.floor((diff / 60000) % 60),
    secs: Math.floor((diff / 1000) % 60),
  };
}

const Ornament = ({ className = "" }) => (
  <div className={`flex items-center justify-center gap-3 ${className}`} aria-hidden="true">
    <span className="h-px w-16 sm:w-24" style={{ background: gold }} />
    <Crown size={16} style={{ color: gold }} />
    <span className="h-px w-16 sm:w-24" style={{ background: gold }} />
  </div>
);

function TierCard({ tier, index, onBuy, buying }) {
  const [qty, setQty] = useState(1);
  const maxQty = Math.min(4, tier.remaining);
  const price = (tier.price_cents / 100).toLocaleString("en-US");
  const featured = tier.lookup_key === "nye_crowned_wolf";

  return (
    <motion.div
      data-testid={`tier-card-${tier.lookup_key}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="relative flex flex-col border p-6 sm:p-7"
      style={{
        background: featured ? ink : parchment2,
        borderColor: featured ? goldHi : `${gold}66`,
        color: featured ? parchment : ink,
        boxShadow: featured ? `0 0 0 1px ${goldHi}, 0 20px 50px -20px rgba(23,19,12,0.5)` : "none",
      }}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] tracking-[0.3em] flex items-center gap-1.5"
          style={{ background: oxblood, color: parchment }}>
          <Crown size={11} /> MOST EXCLUSIVE
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: featured ? goldHi : gold }}>
            {tier.label}
          </div>
          <h3 className="mt-1 text-2xl sm:text-3xl" style={{ fontFamily: "'Bodoni Moda', serif" }}>
            {tier.name}
          </h3>
        </div>
        <div className="text-right">
          <div className="text-2xl sm:text-3xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: featured ? goldHi : ink }}>
            ${price}
          </div>
          <div className="text-[10px] tracking-widest uppercase opacity-60">per ticket</div>
        </div>
      </div>

      <ul className="mt-5 space-y-2 text-sm flex-1" style={{ color: featured ? `${parchment}cc` : `${ink}cc` }}>
        {tier.perks.map((p) => (
          <li key={p} className="flex items-start gap-2">
            <Sparkles size={13} className="mt-0.5 shrink-0" style={{ color: featured ? goldHi : gold }} />
            {p}
          </li>
        ))}
      </ul>

      <div className="mt-5 flex items-center justify-between">
        <span data-testid={`tier-remaining-${tier.lookup_key}`}
          className="text-[11px] tracking-[0.2em] uppercase"
          style={{ fontFamily: "'IBM Plex Mono', monospace", color: tier.sold_out ? oxblood : featured ? goldHi : oxblood }}>
          {tier.sold_out ? "Sold out" : `${tier.remaining} of ${tier.capacity} remain`}
        </span>
        {!tier.sold_out && (
          <div className="flex items-center gap-2" data-testid={`tier-qty-${tier.lookup_key}`}>
            <button aria-label="decrease quantity" data-testid={`tier-qty-minus-${tier.lookup_key}`}
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="w-7 h-7 flex items-center justify-center border transition-colors"
              style={{ borderColor: `${gold}88`, color: featured ? parchment : ink }}>
              <Minus size={12} />
            </button>
            <span className="w-5 text-center text-sm" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{qty}</span>
            <button aria-label="increase quantity" data-testid={`tier-qty-plus-${tier.lookup_key}`}
              onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
              className="w-7 h-7 flex items-center justify-center border transition-colors"
              style={{ borderColor: `${gold}88`, color: featured ? parchment : ink }}>
              <Plus size={12} />
            </button>
          </div>
        )}
      </div>

      <button
        data-testid={`tier-buy-${tier.lookup_key}`}
        disabled={tier.sold_out || buying}
        onClick={() => onBuy(tier, qty)}
        className="mt-5 w-full py-3.5 text-[12px] tracking-[0.3em] uppercase transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{
          background: featured ? goldHi : oxblood,
          color: featured ? ink : parchment,
          fontFamily: "'Manrope', sans-serif", fontWeight: 700,
        }}
      >
        {buying ? <Loader2 size={14} className="animate-spin" /> : null}
        {tier.sold_out ? "The Den Is Full" : buying ? "Opening the Gates…" : "Claim Your Mask"}
      </button>
    </motion.div>
  );
}

export default function NyeEvent() {
  const [data, setData] = useState(null);
  const [buyingKey, setBuyingKey] = useState(null);
  const cd = useEventCountdown();

  useEffect(() => {
    const t0 = setTimeout(() => {
      document.title = "Wolfkeeper New Year's Eve Masquerade Bash — 48 Tickets Only";
    }, 50);
    const params = new URLSearchParams(window.location.search);
    if (params.get("cancelled")) {
      toast.error("Checkout cancelled — your mask is still waiting.", { id: "nye-cancel" });
    }
    getNyeTiers().then(setData).catch(() => toast.error("Could not load ticket inventory."));
    const t = setInterval(() => getNyeTiers().then(setData).catch(() => {}), 20000);
    return () => { clearInterval(t); clearTimeout(t0); };
  }, []);

  const buy = async (tier, qty) => {
    setBuyingKey(tier.lookup_key);
    try {
      const res = await createNyeCheckout({
        lookup_key: tier.lookup_key,
        quantity: qty,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      const msg = e?.response?.status === 409 ? "Those tickets were just claimed — try fewer or another tier." : "Could not open checkout. Please try again.";
      toast.error(msg);
      setBuyingKey(null);
      getNyeTiers().then(setData).catch(() => {});
    }
  };

  const countdown = useMemo(() => [
    { v: cd.days, l: "Days" }, { v: cd.hours, l: "Hours" }, { v: cd.mins, l: "Minutes" }, { v: cd.secs, l: "Seconds" },
  ], [cd]);

  return (
    <div data-testid="nye-page" className="min-h-screen" style={{ background: parchment, color: ink }}>
      {/* Hero */}
      <section className="relative px-5 pt-14 pb-16 sm:pt-20 sm:pb-24 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: `radial-gradient(${ink} 1px, transparent 1px)`, backgroundSize: "22px 22px" }} />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="relative max-w-3xl mx-auto border px-5 py-10 sm:px-12 sm:py-14"
          style={{ borderColor: gold, boxShadow: `inset 0 0 0 1px ${parchment}, inset 0 0 0 3px ${gold}55` }}>
          <Ornament />
          <div className="mt-6 text-[11px] sm:text-xs tracking-[0.5em] uppercase" style={{ color: oxblood, fontFamily: "'IBM Plex Mono', monospace" }}>
            Wolfkeeper University Presents
          </div>
          <h1 className="mt-4 text-4xl sm:text-6xl leading-[1.05]" style={{ fontFamily: "'Bodoni Moda', serif" }}>
            The New Year's Eve
            <span className="block italic" style={{ color: oxblood }}>Masquerade Bash</span>
          </h1>
          <p className="mt-6 max-w-xl mx-auto text-sm sm:text-base leading-relaxed" style={{ color: `${ink}b3` }}>
            One night. Forty-eight masks. A secret Chicago location revealed only to those who hold a ticket.
            Toriano Sanzone hosts an evening of ceremony, champagne, and the crowned pack.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[11px] sm:text-xs tracking-[0.25em] uppercase"
            style={{ fontFamily: "'IBM Plex Mono', monospace", color: `${ink}cc` }}>
            <span className="flex items-center gap-2"><Clock size={13} style={{ color: oxblood }} /> Oct 31, 2026 · 5:55 PM</span>
            <span className="flex items-center gap-2"><MapPin size={13} style={{ color: oxblood }} /> Location revealed once purchased</span>
            <span className="flex items-center gap-2"><Shirt size={13} style={{ color: oxblood }} /> Masquerade attire</span>
          </div>

          {/* Countdown */}
          <div data-testid="nye-countdown" className="mt-10 grid grid-cols-4 gap-2 sm:gap-4 max-w-md mx-auto">
            {countdown.map((c) => (
              <div key={c.l} className="border py-3 sm:py-4" style={{ borderColor: `${gold}66`, background: parchment2 }}>
                <div className="text-2xl sm:text-4xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: oxblood }}>
                  {String(c.v).padStart(2, "0")}
                </div>
                <div className="mt-1 text-[9px] sm:text-[10px] tracking-[0.3em] uppercase opacity-70">{c.l}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Inventory banner */}
      <section className="py-4 text-center" style={{ background: ink, color: parchment }}>
        <div data-testid="nye-inventory-banner" className="text-[11px] sm:text-xs tracking-[0.35em] uppercase"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          {data ? (
            <>Only <span style={{ color: goldHi }}>{data.total_remaining}</span> of {data.total_capacity} tickets remain</>
          ) : "Summoning the guest list…"}
        </div>
      </section>

      {/* Tiers */}
      <section className="px-5 py-16 sm:py-24 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <Ornament />
          <h2 className="mt-4 text-3xl sm:text-5xl" style={{ fontFamily: "'Bodoni Moda', serif" }}>Choose Your Rank</h2>
          <p className="mt-3 text-sm max-w-lg mx-auto" style={{ color: `${ink}b3` }}>
            Five tiers. Fixed inventory. When a tier sells out, the den closes — no exceptions, no waiting list.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {(data?.tiers || []).map((t, i) => (
            <TierCard key={t.lookup_key} tier={t} index={i} onBuy={buy} buying={buyingKey === t.lookup_key} />
          ))}
        </div>
        {!data && (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: gold }} /></div>
        )}
      </section>

      {/* The evening */}
      <section className="px-5 py-16 sm:py-20" style={{ background: oxblood, color: parchment }}>
        <div className="max-w-4xl mx-auto text-center">
          <Drama size={28} className="mx-auto" style={{ color: goldHi }} />
          <h2 className="mt-4 text-3xl sm:text-4xl" style={{ fontFamily: "'Bodoni Moda', serif" }}>Behind the Mask</h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-3 text-sm">
            {[
              { icon: MapPin, t: "The Secret Den", d: "The address is revealed only to ticket holders. Trust the pack." },
              { icon: Drama, t: "Masks Required", d: "Full masquerade attire. No mask, no entry — the night demands ceremony." },
              { icon: Crown, t: "Hosted by the Wolfkeeper", d: "Toriano Sanzone receives the crowned pack personally at the door." },
            ].map((f) => (
              <div key={f.t}>
                <f.icon size={18} className="mx-auto" style={{ color: goldHi }} />
                <div className="mt-3 text-[11px] tracking-[0.3em] uppercase" style={{ color: goldHi }}>{f.t}</div>
                <p className="mt-2 leading-relaxed" style={{ color: `${parchment}cc` }}>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Assurance strip */}
      <section className="px-5 py-10 text-center">
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[11px] tracking-[0.2em] uppercase"
          style={{ fontFamily: "'IBM Plex Mono', monospace", color: `${ink}99` }}>
          <span className="flex items-center gap-2"><ShieldCheck size={13} style={{ color: gold }} /> Secure Stripe checkout</span>
          <span className="flex items-center gap-2"><Sparkles size={13} style={{ color: gold }} /> Instant QR ticket by email</span>
          <span className="flex items-center gap-2"><Clock size={13} style={{ color: gold }} /> Inventory held while you pay</span>
        </div>
        <p className="mt-8 text-[10px] tracking-[0.3em] uppercase" style={{ color: `${ink}66`, fontFamily: "'IBM Plex Mono', monospace" }}>
          Wolfkeeper University · torianosanzone.com
        </p>
      </section>
    </div>
  );
}
